import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { Card, Toggle, PillButton, Chip } from '../../ui';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as XIcon } from '../../assets/icons/x.svg';
import PlanLocked from './PlanLocked';
import './AdminLayout.css';
import { apiErrorMessage } from '../../utils/apiError';

const TIMEOUT_OPTIONS = ['never', 'browser', '5m', '15m', '30m', '1h', '2h', '4h', '8h', '24h'];

// IPv4/IPv6 en notación CIDR. Validación de cliente: la de verdad es
// `isValidCidr` en el servidor (server/utils/cidr.js); esto solo evita un
// viaje de red con un valor claramente mal escrito.
const CIDR_RE = /^([0-9a-fA-F:.]+)\/(\d{1,3})$/;

function Policies() {
  const { t } = useTranslation();
  const { org, has, reloadOrg, orgId } = useOutletContext();
  const canManage = has('policies.manage');

  const [policies, setPolicies] = useState(org?.settings?.policies || {});
  const [newCidr, setNewCidr] = useState('');
  const [cidrError, setCidrError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  // { total, missingFactor } o null si no se pudo cargar (p. ej. 403 porque
  // el propio admin aun no tiene PIN: el aviso lo pinta AdminLayout).
  const [compliance, setCompliance] = useState(null);
  const isBusiness = org?.plan === 'business';

  useEffect(() => { setPolicies(org?.settings?.policies || {}); }, [org]);

  const loadCompliance = useCallback(() => {
    if (!canManage) return;
    api.get(`/org/${orgId}/policies/compliance`)
      .then((res) => setCompliance(res.data))
      .catch(() => setCompliance(null));
  }, [orgId, canManage]);

  useEffect(() => { loadCompliance(); }, [loadCompliance]);

  const save = useCallback(async (updates) => {
    setSaving(true);
    setError('');
    try {
      const res = await api.put(`/org/${orgId}/policies`, updates);
      setPolicies(res.data.policies || {});
      setSavedAt(Date.now());
      reloadOrg();
      loadCompliance();
    } catch (err) {
      setError(apiErrorMessage(err, t, 'errors.generic'));
    } finally {
      setSaving(false);
    }
  }, [orgId, reloadOrg, loadCompliance, t]);

  const addCidr = () => {
    setCidrError('');
    const value = newCidr.trim();
    if (!CIDR_RE.test(value)) { setCidrError(t('admin.policies.invalidCidr')); return; }
    const list = [...(policies.networkAllowlist || []), value];
    save({ networkAllowlist: list });
    setNewCidr('');
  };

  const removeCidr = (value) => {
    const list = (policies.networkAllowlist || []).filter((c) => c !== value);
    save({ networkAllowlist: list });
  };

  if (!canManage) {
    return <p className="sgc-admin__muted">{t('admin.noPermission')}</p>;
  }

  return (
    <div>
      <h2 className="sgc-admin__section-title">{t('admin.tabs.policies')}</h2>
      <p className="sgc-admin__muted">{t('admin.policies.adminsNotice')}</p>

      {error && <p className="sgc-admin__error">{error}</p>}
      {savedAt && !error && <p className="sgc-admin__muted">{t('admin.policies.saved')}</p>}

      <Card className="sgc-admin__form">
        {isBusiness && (
          <div className="sgc-admin__policy-row">
            <span>{t('admin.policies.enforceNetwork')}</span>
            <Toggle checked={!!policies.enforceNetworkForMembers} onChange={(v) => save({ enforceNetworkForMembers: v })} disabled={saving} />
          </div>
        )}
        <div>
          <div className="sgc-admin__policy-row">
            <span>{t('admin.policies.requireSecondFactor')}</span>
            <Toggle checked={!!policies.require2fa} onChange={(v) => save({ require2fa: v })} disabled={saving} />
          </div>
          <p className="sgc-admin__muted">{t('admin.policies.requireSecondFactorHelp')}</p>
          {compliance && (
            <p className="sgc-admin__muted">
              {compliance.missingFactor > 0
                ? t('admin.policies.complianceMissing', { missing: compliance.missingFactor, total: compliance.total })
                : t('admin.policies.complianceOk', { total: compliance.total })}
            </p>
          )}
        </div>
        <div className="sgc-admin__policy-row">
          <span>{t('admin.policies.sessionTimeout')}</span>
          <select
            className="sgc-admin__select"
            value={policies.sessionTimeout || '24h'}
            onChange={(e) => save({ sessionTimeout: e.target.value })}
            disabled={saving}
          >
            {TIMEOUT_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{t(`session.timeouts.${opt}`, opt)}</option>
            ))}
          </select>
        </div>
        <div className="sgc-admin__policy-row">
          <span>{t('admin.policies.minPasswordScore')}</span>
          <input
            type="number" min={0} max={100} className="sgc-admin__select" style={{ width: 80 }}
            value={policies.minPasswordScore ?? 0}
            onChange={(e) => save({ minPasswordScore: parseInt(e.target.value, 10) || 0 })}
            disabled={saving}
          />
        </div>

        {!isBusiness && <PlanLocked orgId={orgId} canBilling={has('billing.manage')} />}

        {isBusiness && (
        <div>
          <p className="sgc-admin__section-title" style={{ fontSize: 13 }}>{t('admin.policies.networkAllowlist')}</p>
          <div className="sgc-admin__cidr-list">
            {(policies.networkAllowlist || []).map((cidr) => (
              <Chip key={cidr} label={cidr} icon={XIcon} onClick={() => removeCidr(cidr)} />
            ))}
          </div>
          <div className="sgc-admin__cidr-add">
            <input
              className="sgc-admin__select" placeholder="203.0.113.0/24" value={newCidr}
              onChange={(e) => setNewCidr(e.target.value)}
            />
            <PillButton size="sm" icon={PlusIcon} onClick={addCidr} disabled={!newCidr.trim()}>{t('admin.policies.addCidr')}</PillButton>
          </div>
          {cidrError && <p className="sgc-admin__error">{cidrError}</p>}
        </div>
        )}
      </Card>
    </div>
  );
}

export default Policies;
